import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import AdminLayout from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, CheckCircle2, AlertTriangle, XCircle, Sparkles, Globe, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Score = ({ value }) => {
  const color = value >= 80 ? "text-emerald-600" : value >= 50 ? "text-amber-600" : "text-red-600";
  const ring = value >= 80 ? "stroke-emerald-500" : value >= 50 ? "stroke-amber-500" : "stroke-red-500";
  const circumference = 2 * Math.PI * 44;
  const offset = circumference - (value / 100) * circumference;
  return (
    <div className="relative h-32 w-32" data-testid="seo-score-ring">
      <svg className="-rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="44" fill="none" stroke="#E5E7EB" strokeWidth="8" />
        <circle cx="50" cy="50" r="44" fill="none" className={ring} strokeWidth="8" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div className={`absolute inset-0 flex flex-col items-center justify-center ${color}`}>
        <span className="text-3xl font-bold">{value}</span>
        <span className="text-xs">/ 100</span>
      </div>
    </div>
  );
};

const AdminSeo = () => {
  const { token } = useAuth();
  const { currentProject } = useProject();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [seo, setSeo] = useState({
    default_og_image: "",
    twitter_handle: "",
    facebook_app_id: "",
    organization_name: "",
    organization_logo: "",
    organization_same_as: [],
    organization_founding_date: "",
    contact_email: "",
  });
  const [faqs, setFaqs] = useState([]);
  const [quality, setQuality] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentProject) return;
    const load = async () => {
      setLoading(true);
      try {
        const [seoRes, qualityRes, projRes] = await Promise.all([
          axios.get(`${API}/public/projects/${currentProject.id}/seo`),
          axios.get(`${API}/projects/${currentProject.id}/seo-quality`, auth),
          axios.get(`${API}/projects`, auth),
        ]);
        const fresh = (projRes.data || []).find((p) => p.id === currentProject.id);
        if (fresh?.seo) setSeo({ ...seo, ...fresh.seo });
        if (Array.isArray(fresh?.faqs)) setFaqs(fresh.faqs);
        else if (Array.isArray(seoRes.data?.faqs)) setFaqs(seoRes.data.faqs);
        setQuality(qualityRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id]);

  const saveSeo = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/projects/${currentProject.id}/seo`, seo, auth);
      toast.success("SEO settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveFaqs = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/projects/${currentProject.id}/faqs`, { faqs }, auth);
      toast.success(`Saved ${faqs.length} FAQ${faqs.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateSameAs = (i, val) => {
    const next = [...(seo.organization_same_as || [])];
    next[i] = val;
    setSeo({ ...seo, organization_same_as: next });
  };
  const addSameAs = () => setSeo({ ...seo, organization_same_as: [...(seo.organization_same_as || []), ""] });
  const removeSameAs = (i) => {
    const next = [...(seo.organization_same_as || [])];
    next.splice(i, 1);
    setSeo({ ...seo, organization_same_as: next });
  };

  const updateFaq = (i, field, val) => {
    const next = [...faqs];
    next[i] = { ...next[i], [field]: val };
    setFaqs(next);
  };
  const addFaq = () => setFaqs([...faqs, { question: "", answer: "" }]);
  const removeFaq = (i) => {
    const next = [...faqs];
    next.splice(i, 1);
    setFaqs(next);
  };

  const articleSchemaSample = useMemo(() => {
    if (!quality?.article_issues) return null;
    const article = quality.article_issues[0];
    if (!article) return null;
    const url = currentProject?.domain ? `https://${currentProject.domain}/article/${article.slug}` : `/article/${article.slug}`;
    return {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: article.title,
      url,
      author: { "@type": "Person", name: "Editorial Team" },
      publisher: {
        "@type": "NewsMediaOrganization",
        name: seo.organization_name || currentProject?.name,
        logo: seo.organization_logo,
      },
    };
  }, [quality, seo, currentProject]);

  const orgSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "NewsMediaOrganization",
      name: seo.organization_name || currentProject?.name,
      url: currentProject?.domain ? `https://${currentProject.domain}` : "",
      logo: seo.organization_logo || undefined,
      sameAs: (seo.organization_same_as || []).filter(Boolean),
      foundingDate: seo.organization_founding_date || undefined,
      email: seo.contact_email || undefined,
      description: currentProject?.description,
    }),
    [seo, currentProject]
  );

  const faqSchema = useMemo(
    () =>
      faqs.length === 0
        ? null
        : {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          },
    [faqs]
  );

  if (!currentProject) {
    return (
      <AdminLayout title="SEO">
        <p className="text-slate-600">Select a project to manage SEO.</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={`SEO — ${currentProject.name}`}>
      {loading ? (
        <p>Loading…</p>
      ) : (
        <div className="space-y-6" data-testid="admin-seo-page">
          {/* Scorecard */}
          <Card>
            <CardContent className="p-6 flex flex-col md:flex-row gap-6 items-center">
              <Score value={quality?.score ?? 0} />
              <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 w-full">
                <Stat label="Published" value={quality?.summary?.published_articles ?? 0} />
                <Stat label="Missing meta" value={quality?.summary?.missing_meta_description ?? 0} negative />
                <Stat label="Missing image" value={quality?.summary?.missing_featured_image ?? 0} negative />
                <Stat label="No tags" value={quality?.summary?.missing_tags ?? 0} negative />
                <CheckRow ok={!!quality?.summary?.has_org_seo_config} label="Organization schema configured" />
                <CheckRow ok={!!quality?.summary?.has_faqs} label="FAQ schema configured" />
                <CheckRow ok={!!currentProject.domain} label="Custom domain set" />
                <CheckRow ok label="JSON-LD on article pages" />
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="org" className="w-full">
            <TabsList>
              <TabsTrigger value="org" data-testid="tab-organization">Organization</TabsTrigger>
              <TabsTrigger value="faqs" data-testid="tab-faqs">FAQ Schema</TabsTrigger>
              <TabsTrigger value="defaults" data-testid="tab-defaults">Defaults</TabsTrigger>
              <TabsTrigger value="schemas" data-testid="tab-schemas">Live Schemas</TabsTrigger>
              <TabsTrigger value="issues" data-testid="tab-issues">Article Issues</TabsTrigger>
              <TabsTrigger value="tools" data-testid="tab-tools">External Testers</TabsTrigger>
            </TabsList>

            {/* Organization */}
            <TabsContent value="org" className="space-y-4">
              <Card>
                <CardHeader><CardTitle>Organization schema</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <Field label="Organization name" value={seo.organization_name} onChange={(v) => setSeo({ ...seo, organization_name: v })} placeholder={currentProject.name} />
                  <Field label="Logo URL" value={seo.organization_logo} onChange={(v) => setSeo({ ...seo, organization_logo: v })} placeholder="https://example.com/logo.png" />
                  <Field label="Founding date" value={seo.organization_founding_date} onChange={(v) => setSeo({ ...seo, organization_founding_date: v })} placeholder="2024-01-01" />
                  <Field label="Contact email" value={seo.contact_email} onChange={(v) => setSeo({ ...seo, contact_email: v })} placeholder="press@example.com" />
                  <div>
                    <Label>Social profile URLs (sameAs)</Label>
                    <div className="space-y-2 mt-2">
                      {(seo.organization_same_as || []).map((u, i) => (
                        <div key={i} className="flex gap-2">
                          <Input value={u} onChange={(e) => updateSameAs(i, e.target.value)} placeholder="https://twitter.com/yoursite" data-testid={`org-sameas-${i}`} />
                          <Button variant="ghost" size="icon" onClick={() => removeSameAs(i)} aria-label="Remove"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" onClick={addSameAs} data-testid="add-sameas-btn"><Plus className="h-4 w-4 mr-1" />Add link</Button>
                    </div>
                  </div>
                  <Button onClick={saveSeo} disabled={saving} data-testid="save-seo-btn">Save organization</Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* FAQs */}
            <TabsContent value="faqs" className="space-y-4">
              <Card>
                <CardHeader><CardTitle>FAQ schema editor</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-600">FAQs are emitted as <code className="bg-slate-100 px-1 rounded">FAQPage</code> JSON-LD on the homepage and rendered as a visible section, helping with rich-result snippets.</p>
                  <div className="space-y-3">
                    {faqs.map((f, i) => (
                      <div key={i} className="border rounded-lg p-4 space-y-2 bg-slate-50">
                        <div className="flex justify-between items-start">
                          <Label className="text-xs uppercase tracking-wider text-slate-500">Q&A #{i + 1}</Label>
                          <Button variant="ghost" size="icon" onClick={() => removeFaq(i)} data-testid={`remove-faq-${i}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                        <Input value={f.question} placeholder="Question" onChange={(e) => updateFaq(i, "question", e.target.value)} data-testid={`faq-q-${i}`} />
                        <Textarea value={f.answer} placeholder="Answer" rows={3} onChange={(e) => updateFaq(i, "answer", e.target.value)} data-testid={`faq-a-${i}`} />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={addFaq} data-testid="add-faq-btn"><Plus className="h-4 w-4 mr-1" /> Add FAQ</Button>
                    <Button onClick={saveFaqs} disabled={saving} data-testid="save-faqs-btn">Save FAQs</Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Defaults */}
            <TabsContent value="defaults" className="space-y-4">
              <Card>
                <CardHeader><CardTitle>Default social sharing</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <Field label="Default OG image (used when an article has none)" value={seo.default_og_image} onChange={(v) => setSeo({ ...seo, default_og_image: v })} placeholder="https://example.com/og.png" />
                  <Field label="Twitter handle" value={seo.twitter_handle} onChange={(v) => setSeo({ ...seo, twitter_handle: v })} placeholder="@yoursite" />
                  <Field label="Facebook app ID (optional)" value={seo.facebook_app_id} onChange={(v) => setSeo({ ...seo, facebook_app_id: v })} placeholder="123456789" />
                  <Button onClick={saveSeo} disabled={saving} data-testid="save-defaults-btn">Save defaults</Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Live schemas */}
            <TabsContent value="schemas" className="space-y-4">
              <SchemaBlock title="Organization (NewsMediaOrganization)" jsonLd={orgSchema} testId="schema-organization" />
              {faqSchema && <SchemaBlock title="FAQ Page" jsonLd={faqSchema} testId="schema-faq" />}
              {articleSchemaSample && <SchemaBlock title="Sample NewsArticle (from your articles)" jsonLd={articleSchemaSample} testId="schema-article" />}
            </TabsContent>

            {/* Issues */}
            <TabsContent value="issues" className="space-y-4">
              <Card>
                <CardHeader><CardTitle>Articles needing attention</CardTitle></CardHeader>
                <CardContent>
                  {quality?.article_issues?.length === 0 ? (
                    <p className="text-emerald-700 inline-flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> All published articles look good.</p>
                  ) : (
                    <div className="divide-y">
                      {quality?.article_issues?.map((a) => (
                        <div key={a.id} className="py-3 flex items-start justify-between gap-4" data-testid={`issue-row-${a.id}`}>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate">{a.title}</p>
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {a.issues.map((iss) => <Badge key={iss} variant="outline" className="text-amber-700 border-amber-200">{iss}</Badge>)}
                            </div>
                          </div>
                          <a href={`/article/${a.slug}`} target="_blank" rel="noreferrer" className="text-sm text-blue-600 inline-flex items-center gap-1 shrink-0"><ExternalLink className="h-3.5 w-3.5" /> View</a>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* External Testers */}
            <TabsContent value="tools" className="space-y-4">
              <Card>
                <CardHeader><CardTitle>External validators</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <Tester
                    name="Google Rich Results Test"
                    desc="Best for verifying NewsArticle, FAQPage, BreadcrumbList eligibility"
                    url={`https://search.google.com/test/rich-results?url=${encodeURIComponent(currentProject.domain ? `https://${currentProject.domain}/` : "")}`}
                  />
                  <Tester
                    name="Schema.org Validator"
                    desc="Strict schema.org compliance check"
                    url={`https://validator.schema.org/#url=${encodeURIComponent(currentProject.domain ? `https://${currentProject.domain}/` : "")}`}
                  />
                  <Tester
                    name="Facebook Sharing Debugger"
                    desc="Preview the OpenGraph card Facebook will show"
                    url={`https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(currentProject.domain ? `https://${currentProject.domain}/` : "")}`}
                  />
                  <Tester
                    name="Twitter Card Validator"
                    desc="Preview the Twitter card and image"
                    url={`https://cards-dev.twitter.com/validator`}
                  />
                  <Tester
                    name="PageSpeed Insights"
                    desc="Core Web Vitals + accessibility + SEO score"
                    url={`https://pagespeed.web.dev/report?url=${encodeURIComponent(currentProject.domain ? `https://${currentProject.domain}/` : "")}`}
                  />
                  <Tester
                    name="Sitemap"
                    desc="View your generated sitemap.xml"
                    url={currentProject.domain ? `https://${currentProject.domain}/sitemap.xml` : `/sitemap.xml`}
                  />
                  <Tester
                    name="robots.txt"
                    desc="View robots policy (AI crawlers welcomed)"
                    url={currentProject.domain ? `https://${currentProject.domain}/robots.txt` : `/robots.txt`}
                  />
                  <Tester
                    name="llms.txt"
                    desc="AI-readable site overview (improves AI SEO citations)"
                    url={currentProject.domain ? `https://${currentProject.domain}/llms.txt` : `/llms.txt`}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </AdminLayout>
  );
};

const Stat = ({ label, value, negative }) => (
  <div className="flex flex-col">
    <span className="text-2xl font-bold" style={{ color: negative && value > 0 ? "#DC2626" : "#0F172A" }}>{value}</span>
    <span className="text-xs text-slate-500 uppercase tracking-wider mt-1">{label}</span>
  </div>
);

const CheckRow = ({ ok, label }) => (
  <div className="flex items-center gap-2 text-sm">
    {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-amber-600" />}
    <span className={ok ? "text-slate-700" : "text-slate-500"}>{label}</span>
  </div>
);

const Field = ({ label, value, onChange, placeholder }) => (
  <div>
    <Label className="mb-1.5 block">{label}</Label>
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

const Tester = ({ name, desc, url }) => (
  <a href={url} target="_blank" rel="noreferrer" className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 transition" data-testid={`tester-${name.replace(/\s+/g, "-").toLowerCase()}`}>
    <div>
      <p className="font-medium">{name}</p>
      <p className="text-xs text-slate-500">{desc}</p>
    </div>
    <ExternalLink className="h-4 w-4 text-slate-400" />
  </a>
);

const SchemaBlock = ({ title, jsonLd, testId }) => {
  const text = JSON.stringify(jsonLd, null, 2);
  const validation = validateSchema(jsonLd);
  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-base">{title}</CardTitle>
          <div className="flex items-center gap-2">
            {validation.ok ? (
              <Badge className="bg-emerald-100 text-emerald-700">Valid</Badge>
            ) : (
              <Badge variant="outline" className="text-amber-700 border-amber-200">{validation.issues.length} issue{validation.issues.length === 1 ? "" : "s"}</Badge>
            )}
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }}>Copy</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!validation.ok && (
          <div className="mb-3 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <ul className="list-disc list-inside">{validation.issues.map((i) => <li key={i}>{i}</li>)}</ul>
          </div>
        )}
        <pre className="bg-slate-900 text-emerald-300 text-xs p-4 rounded-lg overflow-auto max-h-96" data-testid={testId}>{text}</pre>
      </CardContent>
    </Card>
  );
};

/** Lightweight schema.org validator — checks required fields per @type */
function validateSchema(obj) {
  const issues = [];
  if (!obj) return { ok: false, issues: ["No data"] };
  if (!obj["@context"]) issues.push("Missing @context");
  if (!obj["@type"]) issues.push("Missing @type");
  const t = obj["@type"];
  if (t === "NewsArticle") {
    if (!obj.headline) issues.push("NewsArticle: headline required");
    if (!obj.author) issues.push("NewsArticle: author required");
    if (!obj.publisher) issues.push("NewsArticle: publisher required");
  }
  if (t === "NewsMediaOrganization") {
    if (!obj.name) issues.push("Organization: name required");
    if (!obj.url) issues.push("Organization: url recommended");
    if (!obj.logo) issues.push("Organization: logo recommended for AMP / Google News");
  }
  if (t === "FAQPage") {
    if (!Array.isArray(obj.mainEntity) || obj.mainEntity.length === 0) issues.push("FAQPage: mainEntity must contain at least one Question");
    (obj.mainEntity || []).forEach((q, i) => {
      if (!q.name) issues.push(`Q${i + 1}: missing name (question text)`);
      if (!q.acceptedAnswer?.text) issues.push(`Q${i + 1}: missing acceptedAnswer.text`);
    });
  }
  return { ok: issues.length === 0, issues };
}

export default AdminSeo;
